import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { FolderOpen, Plus, Trash2, Eye, Download, X, Upload, Image, ChevronRight } from 'lucide-react';

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function ProjectPhotos({ projectId }) {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFolder, setActiveFolder] = useState(null); // { id, name }
  const [photos, setPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [deleteFolder, setDeleteFolder] = useState(null);
  const [deletePhoto, setDeletePhoto] = useState(null);
  const [viewPhoto, setViewPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const loadFolders = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${projectId}/photos`);
      setFolders(res.data || []);
    } catch (err) { console.error('Failed to load photos folders:', err); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { loadFolders(); }, [loadFolders]);

  const loadPhotos = async (folder) => {
    setActiveFolder(folder);
    setLoadingPhotos(true);
    try {
      const res = await api.get(`/projects/${projectId}/photos/folders/${folder.id}`);
      setPhotos(res.data || []);
    } catch { toast.error('Failed to load photos'); }
    finally { setLoadingPhotos(false); }
  };

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) { toast.error('Folder name required'); return; }
    try {
      const res = await api.post(`/projects/${projectId}/photos/folders`, { name });
      setFolders(prev => [...prev, res.data]);
      toast.success(`Folder "${name}" created`);
    } catch { toast.error('Failed to create folder'); }
    setCreatingFolder(false);
    setNewFolderName('');
  };

  const confirmDeleteFolder = async () => {
    if (!deleteFolder) return;
    try {
      await api.delete(`/projects/${projectId}/photos/folders/${deleteFolder.id}`);
      setFolders(prev => prev.filter(f => f.id !== deleteFolder.id));
      if (activeFolder?.id === deleteFolder.id) { setActiveFolder(null); setPhotos([]); }
      toast.success('Folder deleted');
    } catch { toast.error('Delete failed'); }
    setDeleteFolder(null);
  };

  const uploadPhotos = async (files) => {
    if (!activeFolder) return;
    const accepted = Array.from(files).filter(f => /^image\/(jpeg|png|webp|heic|heif)$/.test(f.type));
    if (accepted.length === 0) { toast.error('Only JPG, PNG, WEBP, HEIC files accepted'); return; }
    setUploading(true);
    for (const file of accepted) {
      try {
        const dataUrl = await readFileAsDataUrl(file);
        const res = await api.post(`/projects/${projectId}/photos/folders/${activeFolder.id}/photos`, {
          name: file.name, size: file.size, data_url: dataUrl
        });
        setPhotos(prev => [...prev, res.data]);
        setFolders(prev => prev.map(f => f.id === activeFolder.id ? { ...f, photo_count: (f.photo_count || 0) + 1 } : f));
      } catch { toast.error(`Failed: ${file.name}`); }
    }
    toast.success(`${accepted.length} photo(s) uploaded`);
    setUploading(false);
  };

  const confirmDeletePhoto = async () => {
    if (!deletePhoto || !activeFolder) return;
    try {
      await api.delete(`/projects/${projectId}/photos/folders/${activeFolder.id}/photos/${deletePhoto.id}`);
      setPhotos(prev => prev.filter(p => p.id !== deletePhoto.id));
      setFolders(prev => prev.map(f => f.id === activeFolder.id ? { ...f, photo_count: Math.max(0, (f.photo_count || 1) - 1) } : f));
      toast.success('Photo deleted');
    } catch { toast.error('Delete failed'); }
    setDeletePhoto(null);
  };

  const downloadPhoto = (photo) => {
    const a = document.createElement('a');
    a.href = photo.data_url;
    a.download = photo.name || 'photo';
    a.click();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadPhotos(e.dataTransfer.files);
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="w-5 h-5 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" /></div>;

  // ─── Folder Content View ──────────────────
  if (activeFolder) {
    return (
      <div className="space-y-4" data-testid="photos-folder-view">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-sm">
          <button onClick={() => { setActiveFolder(null); setPhotos([]); }} className="text-[#3A8C8C] hover:underline font-medium" data-testid="photos-breadcrumb-root">
            Photos
          </button>
          <ChevronRight size={14} strokeWidth={1.5} className="text-[#6B7280]" />
          <span className="text-[#2C2C2E] font-semibold">{activeFolder.name}</span>
        </div>

        {/* Photo grid or empty upload zone */}
        {loadingPhotos ? (
          <div className="flex items-center justify-center h-32"><div className="w-5 h-5 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" /></div>
        ) : photos.length === 0 && !uploading ? (
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
            className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${dragOver ? 'border-[#3A8C8C] bg-[#3A8C8C]/5' : 'border-[#D4D0C8] hover:border-[#3A8C8C]/50'}`}
            data-testid="photos-empty-upload"
          >
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" multiple className="hidden"
              onChange={e => { uploadPhotos(e.target.files); e.target.value = ''; }} />
            <Upload size={32} strokeWidth={1.5} className={`mx-auto mb-3 ${dragOver ? 'text-[#3A8C8C]' : 'text-[#D4D0C8]'}`} />
            <p className="text-sm font-medium text-[#2C2C2E]">Drag & drop photos here</p>
            <p className="text-xs text-[#6B7280] mt-1">or click to browse · JPG, PNG, WEBP, HEIC</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="photos-grid">
            {photos.map(photo => (
              <div key={photo.id} className="relative aspect-square rounded-xl overflow-hidden border border-[#E8E6E1] group bg-[#F5F3EF]"
                data-testid={`photo-tile-${photo.id}`}>
                <img src={photo.data_url} alt={photo.name} className="w-full h-full object-cover" loading="lazy" />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button onClick={() => setViewPhoto(photo)} className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors" title="View"
                    data-testid={`photo-view-${photo.id}`}>
                    <Eye size={16} strokeWidth={1.5} className="text-white" />
                  </button>
                  <button onClick={() => downloadPhoto(photo)} className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors" title="Download"
                    data-testid={`photo-download-${photo.id}`}>
                    <Download size={16} strokeWidth={1.5} className="text-white" />
                  </button>
                  <button onClick={() => setDeletePhoto(photo)} className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors" title="Delete"
                    data-testid={`photo-delete-${photo.id}`}>
                    <Trash2 size={16} strokeWidth={1.5} className="text-white" />
                  </button>
                </div>
                <p className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[9px] px-2 py-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                  {photo.name}
                </p>
              </div>
            ))}
            {/* Add more tile */}
            <div
              onClick={() => fileRef.current?.click()}
              className="aspect-square rounded-xl border-2 border-dashed border-[#D4D0C8] hover:border-[#3A8C8C]/50 flex flex-col items-center justify-center cursor-pointer transition-colors"
              data-testid="photos-add-more"
            >
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" multiple className="hidden"
                onChange={e => { uploadPhotos(e.target.files); e.target.value = ''; }} />
              <Plus size={24} strokeWidth={1.5} className="text-[#D4D0C8] mb-1" />
              <span className="text-[10px] text-[#6B7280]">{uploading ? 'Uploading...' : 'Add more'}</span>
            </div>
          </div>
        )}

        {/* Photo Viewer overlay */}
        {viewPhoto && (
          <div className="fixed inset-0 z-50 bg-black/90 flex flex-col" onClick={e => { if (e.target === e.currentTarget) setViewPhoto(null); }}
            data-testid="photo-viewer-overlay">
            <div className="flex items-center justify-between px-5 py-3 bg-black/50 backdrop-blur-sm">
              <span className="text-sm text-white font-medium truncate max-w-[400px]">{viewPhoto.name}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => downloadPhoto(viewPhoto)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 text-white text-xs hover:bg-white/20 transition-colors"
                  data-testid="photo-viewer-download">
                  <Download size={13} /> Download
                </button>
                <button onClick={() => setViewPhoto(null)} className="p-2 rounded-lg hover:bg-white/10 transition-colors" data-testid="photo-viewer-close">
                  <X size={18} strokeWidth={1.5} className="text-white" />
                </button>
              </div>
            </div>
            <div className="flex-1 flex items-center justify-center p-6" onClick={e => { if (e.target === e.currentTarget) setViewPhoto(null); }}>
              <img src={viewPhoto.data_url} alt={viewPhoto.name} className="max-w-full max-h-full object-contain rounded-lg" />
            </div>
          </div>
        )}

        {/* Delete photo confirmation */}
        <Dialog open={!!deletePhoto} onOpenChange={() => setDeletePhoto(null)}>
          <DialogContent className="max-w-xs">
            <DialogHeader><DialogTitle className="font-['Manrope'] text-base">Delete Photo?</DialogTitle></DialogHeader>
            <p className="text-xs text-[#6B7280] mt-1"><span className="font-medium text-[#2C2C2E]">{deletePhoto?.name}</span> will be permanently deleted.</p>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setDeletePhoto(null)} className="flex-1 h-9 rounded-lg border border-[#D4D0C8] text-xs font-medium text-[#6B7280] hover:bg-[#E8E6E1] transition-colors">Cancel</button>
              <button onClick={confirmDeletePhoto} className="flex-1 h-9 rounded-lg bg-[#C4727F] text-white text-xs font-medium hover:bg-[#B5616E] transition-colors" data-testid="photo-delete-confirm">Delete</button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ─── Folder List View ─────────────────────
  return (
    <div className="space-y-4" data-testid="photos-folder-list">
      <div className="flex items-center justify-between">
        <h4 className="section-heading text-sm font-semibold font-['Manrope']">Photos</h4>
        <button onClick={() => { setCreatingFolder(true); setNewFolderName(''); }}
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] transition-colors"
          data-testid="photos-new-folder-btn">
          <Plus size={14} strokeWidth={1.5} /> New Folder
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="photos-folder-grid">
        {folders.map(folder => (
          <div key={folder.id}
            onClick={() => loadPhotos(folder)}
            className="glass-card rounded-xl p-4 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all group"
            data-testid={`folder-card-${folder.id}`}>
            <div className="flex items-start justify-between mb-2">
              <FolderOpen size={24} strokeWidth={1.5} className="text-[#C67D5B]" />
              <button onClick={e => { e.stopPropagation(); setDeleteFolder(folder); }}
                className="p-1 rounded hover:bg-[#C4727F]/10 opacity-0 group-hover:opacity-100 transition-all"
                data-testid={`folder-delete-${folder.id}`}>
                <Trash2 size={13} strokeWidth={1.5} className="text-[#C4727F]" />
              </button>
            </div>
            <p className="text-sm font-medium text-[#2C2C2E] truncate">{folder.name}</p>
            <p className="text-[10px] text-[#6B7280] mt-0.5">{folder.photo_count || 0} photos</p>
          </div>
        ))}

        {/* New Folder tile (inline create) */}
        {creatingFolder ? (
          <div className="glass-card rounded-xl p-4 border-2 border-[#3A8C8C]">
            <FolderOpen size={24} strokeWidth={1.5} className="text-[#3A8C8C] mb-2" />
            <Input autoFocus value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
              placeholder="Folder name" className="h-8 text-sm rounded-lg border-[#D4D0C8] mb-2"
              data-testid="new-folder-name-input"
              onKeyDown={e => {
                if (e.key === 'Enter') createFolder();
                if (e.key === 'Escape') setCreatingFolder(false);
              }} />
            <div className="flex gap-1.5">
              <button onClick={() => setCreatingFolder(false)} className="flex-1 h-7 rounded-lg border border-[#D4D0C8] text-[10px] text-[#6B7280] hover:bg-[#E8E6E1] transition-colors">Cancel</button>
              <button onClick={createFolder} className="flex-1 h-7 rounded-lg bg-[#3A8C8C] text-white text-[10px] hover:bg-[#2E7A7A] transition-colors" data-testid="create-folder-btn">Create</button>
            </div>
          </div>
        ) : (
          <div
            onClick={() => { setCreatingFolder(true); setNewFolderName(''); }}
            className="rounded-xl border-2 border-dashed border-[#D4D0C8] hover:border-[#3A8C8C]/50 p-4 flex flex-col items-center justify-center cursor-pointer transition-colors min-h-[120px]"
            data-testid="photos-new-folder-tile"
          >
            <Plus size={24} strokeWidth={1.5} className="text-[#D4D0C8] mb-1" />
            <span className="text-xs text-[#6B7280]">New Folder</span>
          </div>
        )}
      </div>

      {folders.length === 0 && !creatingFolder && (
        <div className="text-center py-10">
          <Image size={36} strokeWidth={1.5} className="text-[#D4D0C8] mx-auto mb-3" />
          <p className="text-sm text-[#6B7280]">No photo folders yet — create your first folder to start organising.</p>
        </div>
      )}

      {/* Delete folder confirmation */}
      <Dialog open={!!deleteFolder} onOpenChange={() => setDeleteFolder(null)}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle className="font-['Manrope'] text-base">Delete Folder?</DialogTitle></DialogHeader>
          <p className="text-xs text-[#6B7280] mt-1">
            <span className="font-medium text-[#2C2C2E]">{deleteFolder?.name}</span> and all photos inside will be permanently deleted.
          </p>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setDeleteFolder(null)} className="flex-1 h-9 rounded-lg border border-[#D4D0C8] text-xs font-medium text-[#6B7280] hover:bg-[#E8E6E1] transition-colors">Cancel</button>
            <button onClick={confirmDeleteFolder} className="flex-1 h-9 rounded-lg bg-[#C4727F] text-white text-xs font-medium hover:bg-[#B5616E] transition-colors" data-testid="folder-delete-confirm">Delete</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
